/**
 * Read-only tracking template / UTM parameters view (#4379).
 *
 * Surfaces a campaign's Google Ads URL-tracking configuration: the parsed UTM parameters, the raw
 * final URL suffix (where GlitchAds writes its default UTM string at campaign creation), and the
 * campaign-level tracking template (a click-tracking redirect a customer may set in the Google Ads
 * UI). v1 is surface-only: there are no edit controls. Values are read live from Google Ads.
 */

import { Link2 } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';

import { CampaignInfoForm, type CampaignInfoField } from '@/components/campaign/detail/info-form';
import { DataTable } from '@/components/glitch-ui/data-table';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { useCampaignTrackingTemplateQuery } from '@/lib/query-options';
import type { UtmParameter } from '@/lib/apis';

interface TrackingTemplateViewProps {
  campaignId: number;
}

const UTM_COLUMNS: ColumnDef<UtmParameter, unknown>[] = [
  {
    accessorKey: 'key',
    header: 'Parameter',
    cell: ({ row }) => <span className="font-mono text-xs text-gray-900">{row.original.key}</span>,
  },
  {
    accessorKey: 'value',
    header: 'Value',
    cell: ({ row }) => <span className="font-mono text-xs text-gray-700">{row.original.value}</span>,
  },
];

export const TrackingTemplateView: React.FC<TrackingTemplateViewProps> = ({ campaignId }) => {
  const { data, isLoading, isError } = useCampaignTrackingTemplateQuery(campaignId);

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading tracking settings...</p>;
  }

  const emptyIcon = <Link2 className="h-12 w-12 text-gray-400" strokeWidth={1} />;

  if (isError || !data) {
    return (
      <EmptyState
        icon={emptyIcon}
        title="Tracking Settings Unavailable"
        description="Tracking settings could not be read from Google Ads. Try again shortly."
      />
    );
  }

  if (!data.is_published) {
    return (
      <EmptyState
        icon={emptyIcon}
        title="Not Published Yet"
        description="Tracking template and UTM parameters appear here once the campaign is live in Google Ads."
      />
    );
  }

  const hasAnyTracking =
    data.utm_parameters.length > 0 || !!data.final_url_suffix || !!data.tracking_url_template;

  if (!hasAnyTracking) {
    return (
      <EmptyState
        icon={emptyIcon}
        title="No Tracking Configured"
        description="No tracking template or UTM parameters are set on this campaign in Google Ads."
      />
    );
  }

  const rawFields: CampaignInfoField[] = [
    { label: 'Final URL Suffix', value: data.final_url_suffix ?? 'Not set', monospace: true },
    { label: 'Tracking Template', value: data.tracking_url_template ?? 'Not set', monospace: true },
  ];

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        URL tracking is managed in Google Ads and shown here for reference. These values are read-only.
      </p>

      {data.utm_parameters.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">UTM Parameters</h3>
          <DataTable data={data.utm_parameters} columns={UTM_COLUMNS} pageSize={10} />
        </div>
      )}

      <CampaignInfoForm fields={rawFields} />
    </div>
  );
};
